import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TutorSidebar from './components/TutorSidebar';
import SessionModal, { SessionFormData } from './components/SessionModal';
import ResourceModal, { ResourceFormData } from './components/ResourceModal';
import { tutorProfile, tutorStats, tutorSessions as initialSessions, tutorStudents, tutorMessages, tutorResources as initialResources } from '@/mocks/tutor';

interface Session {
  id: number;
  title: string;
  date: string;
  time: string;
  duration: string;
  students: number;
  maxStudents: number;
  status: string;
  level: string;
  board: string;
  notes: string;
  topic?: string;
  sessionType?: string;
  platform?: string;
  meetingLink?: string;
}

interface Resource {
  id: number;
  title: string;
  type: string;
  size: string;
  uploaded: string;
  downloads: number;
  subject?: string;
  level?: string;
  board?: string;
  visibility?: string;
  description?: string;
  tags?: string;
}

export default function TutorDashboardPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const [replyText, setReplyText] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [sessions, setSessions] = useState<Session[]>(initialSessions);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingSession, setEditingSession] = useState<Session | null>(null);

  const [resources, setResources] = useState<Resource[]>(initialResources);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceModalMode, setResourceModalMode] = useState<'create' | 'edit'>('create');
  const [editingResource, setEditingResource] = useState<Resource | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (!userStr || !token) {
        navigate('/tutor/login', { replace: true });
        return;
      }
      const user = JSON.parse(userStr);
      if (user.role !== 'tutor') {
        navigate('/tutor/login', { replace: true });
        return;
      }
    } catch (e) {
      navigate('/tutor/login', { replace: true });
    }
  }, [navigate]);

  const openCreateModal = () => {
    setEditingSession(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (session: Session) => {
    setEditingSession(session);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleSessionSubmit = (data: SessionFormData) => {
    if (modalMode === 'create') {
      const newSession: Session = {
        id: Date.now(),
        title: data.title,
        date: data.date,
        time: data.time,
        duration: data.duration,
        students: 0,
        maxStudents: data.maxStudents,
        status: 'upcoming',
        level: data.level,
        board: data.board,
        notes: data.notes,
        topic: data.topic,
        sessionType: data.sessionType,
        platform: data.platform,
        meetingLink: data.meetingLink,
      };
      setSessions((prev) => [newSession, ...prev]);
    } else if (editingSession) {
      setSessions((prev) =>
        prev.map((s) =>
          s.id === editingSession.id
            ? {
              ...s,
              title: data.title,
              date: data.date,
              time: data.time,
              duration: data.duration,
              maxStudents: data.maxStudents,
              level: data.level,
              board: data.board,
              notes: data.notes,
              status: data.status,
              topic: data.topic,
              sessionType: data.sessionType,
              platform: data.platform,
              meetingLink: data.meetingLink,
            }
            : s
        )
      );
    }
  };

  const openCreateResourceModal = () => {
    setEditingResource(null);
    setResourceModalMode('create');
    setResourceModalOpen(true);
  };

  const openEditResourceModal = (resource: Resource) => {
    setEditingResource(resource);
    setResourceModalMode('edit');
    setResourceModalOpen(true);
  };

  const handleResourceSubmit = (data: ResourceFormData) => {
    if (resourceModalMode === 'create') {
      const newResource: Resource = {
        id: Date.now(),
        title: data.title,
        type: data.resourceType,
        size: '—',
        uploaded: new Date().toISOString().split('T')[0],
        downloads: 0,
        subject: data.subject,
        level: data.level,
        board: data.board,
        visibility: data.visibility,
        description: data.description,
        tags: data.tags,
      };
      setResources((prev) => [newResource, ...prev]);
    } else if (editingResource) {
      setResources((prev) =>
        prev.map((r) =>
          r.id === editingResource.id
            ? {
              ...r,
              title: data.title,
              type: data.resourceType,
              subject: data.subject,
              level: data.level,
              board: data.board,
              visibility: data.visibility,
              description: data.description,
              tags: data.tags,
            }
            : r
        )
      );
    }
  };

  const handleDeleteResource = (id: number) => {
    setResources((prev) => prev.filter((r) => r.id !== id));
  };

  const getResourceTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF': return { icon: 'ri-file-pdf-line', color: 'text-red-500 bg-red-50' };
      case 'DOCX': return { icon: 'ri-file-word-line', color: 'text-blue-500 bg-blue-50' };
      case 'PPT': return { icon: 'ri-file-ppt-line', color: 'text-orange-500 bg-orange-50' };
      case 'Spreadsheet': return { icon: 'ri-file-excel-line', color: 'text-emerald-600 bg-emerald-50' };
      case 'Video': return { icon: 'ri-video-line', color: 'text-purple-500 bg-purple-50' };
      default: return { icon: 'ri-file-line', color: 'text-slate-500 bg-slate-100' };
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="flex flex-col gap-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                Welcome back, {tutorProfile.name.split(' ')[1]}!
              </h2>
              <p className="text-slate-500 text-sm">Here&apos;s your teaching summary for today.</p>
            </div>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {tutorStats.map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-slate-100 p-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-lg mb-3">
                    <i className={`${s.icon} text-emerald-700 text-lg`}></i>
                  </div>
                  <p className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{s.value}</p>
                  <p className="text-slate-500 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
            {/* Upcoming sessions */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Upcoming Sessions</h3>
              <div className="flex flex-col gap-3">
                {sessions.filter((s) => s.status === 'upcoming').map((session) => (
                  <div key={session.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 flex-shrink-0 flex flex-col items-center justify-center bg-emerald-700 rounded-lg text-white">
                      <span className="text-sm font-bold leading-none">{session.date.split('-')[2]}</span>
                      <span className="text-xs opacity-80">{new Date(session.date).toLocaleString('default', { month: 'short' })}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{session.title}</p>
                      <p className="text-xs text-slate-500">{session.time} · {session.duration} · {session.board}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-slate-700">{session.students}/{session.maxStudents}</p>
                      <p className="text-xs text-slate-400">students</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Recent messages */}
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="font-bold text-slate-800 text-sm mb-4">Recent Messages</h3>
              <div className="flex flex-col gap-3">
                {tutorMessages.slice(0, 3).map((msg) => (
                  <div key={msg.id} className="flex items-start gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold ${msg.unread ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {msg.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-700">{msg.from}</p>
                        <span className="text-xs text-slate-400 flex-shrink-0">{msg.time}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{msg.message}</p>
                    </div>
                    {msg.unread && <div className="w-2 h-2 bg-emerald-600 rounded-full flex-shrink-0 mt-1.5"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'sessions':
        return (
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>My Sessions</h2>
                <p className="text-slate-500 text-sm">{sessions.filter((s) => s.status === 'upcoming').length} upcoming sessions</p>
              </div>
              <button
                onClick={openCreateModal}
                className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Request Session
              </button>
            </div>
            <div className="flex flex-col gap-3">
              {sessions.map((session) => (
                <div key={session.id} className="bg-white rounded-xl border border-slate-100 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex-shrink-0 flex flex-col items-center justify-center bg-emerald-700 rounded-xl text-white">
                      <span className="text-base font-bold leading-none">{session.date.split('-')[2]}</span>
                      <span className="text-xs opacity-80">{new Date(session.date).toLocaleString('default', { month: 'short' })}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-bold text-slate-800 text-sm">{session.title}</p>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${session.status === 'upcoming' ? 'bg-emerald-50 text-emerald-700' : session.status === 'cancelled' ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-500'}`}>{session.status}</span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{session.board}</span>
                        {session.sessionType && (
                          <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">{session.sessionType}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{session.date} · {session.time} · {session.duration}</p>
                      {session.topic && <p className="text-xs text-slate-500 mb-1"><i className="ri-focus-line mr-1"></i>{session.topic}</p>}
                      {session.platform && (
                        <p className="text-xs text-slate-400"><i className="ri-video-line mr-1"></i>{session.platform}
                          {session.meetingLink && (
                            <a href={session.meetingLink} target="_blank" rel="nofollow noreferrer" className="ml-2 text-emerald-600 hover:underline">Join Link</a>
                          )}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 italic mt-1">{session.notes}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-700">{session.students}/{session.maxStudents}</p>
                        <p className="text-xs text-slate-400">students</p>
                        <div className="w-16 bg-slate-100 rounded-full h-1 mt-1">
                          <div className="bg-emerald-600 h-1 rounded-full" style={{ width: `${(session.students / session.maxStudents) * 100}%` }}></div>
                        </div>
                      </div>
                      <button
                        onClick={() => openEditModal(session)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i> Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'students':
        return (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>My Students</h2>
              <p className="text-slate-500 text-sm">{tutorStudents.length} students assigned to you</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {tutorStudents.map((s) => (
                <div key={s.id} className="bg-white rounded-xl border border-slate-100 p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full text-sm font-bold text-emerald-700 flex-shrink-0">
                      {s.name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-800 text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${s.level === 'GCSE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{s.level}</span>
                  </div>
                  <div className="flex gap-4 text-xs text-slate-500 mb-3">
                    <span><i className="ri-book-line mr-1"></i>{s.year}</span>
                    <span><i className="ri-award-line mr-1"></i>{s.board}</span>
                    <span><i className="ri-video-line mr-1"></i>{s.sessions} sessions</span>
                    <span><i className="ri-time-line mr-1"></i>{s.lastSeen}</span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-slate-500">Progress</span>
                      <span className="text-xs font-semibold text-slate-700">{s.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${s.progress >= 75 ? 'bg-emerald-600' : s.progress >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${s.progress}%` }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'resources':
        return (
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>My Resources</h2>
                <p className="text-slate-500 text-sm">{resources.length} resources uploaded</p>
              </div>
              <button
                onClick={openCreateResourceModal}
                className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-upload-2-line"></i> Upload Resource
              </button>
            </div>
            <div className="flex flex-col gap-3">
              {resources.map((r) => {
                const typeInfo = getResourceTypeIcon(r.type);
                return (
                  <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${typeInfo.color}`}>
                        <i className={`${typeInfo.icon} text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="font-semibold text-slate-800 text-sm">{r.title}</p>
                          {r.visibility && (
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.visibility === 'Public' ? 'bg-emerald-50 text-emerald-700' : r.visibility === 'Private' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-600'}`}>
                              {r.visibility === 'Public' ? <><i className="ri-global-line mr-0.5"></i>Public</> : r.visibility === 'Private' ? <><i className="ri-lock-line mr-0.5"></i>Private</> : <><i className="ri-group-line mr-0.5"></i>Students</>}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mb-1">
                          {r.type} · {r.size} · Uploaded {r.uploaded}
                          {r.level && <> · <span className="text-slate-500">{r.level}</span></>}
                          {r.board && <> · <span className="text-slate-500">{r.board}</span></>}
                        </p>
                        {r.subject && (
                          <p className="text-xs text-slate-500 mb-1"><i className="ri-book-open-line mr-1 text-emerald-600"></i>{r.subject}</p>
                        )}
                        {r.tags && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.tags.split(',').map((tag) => tag.trim()).filter(Boolean).map((tag) => (
                              <span key={tag} className="text-xs bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded-full">#{tag}</span>
                            ))}
                          </div>
                        )}
                        {r.description && (
                          <p className="text-xs text-slate-400 italic mt-1 line-clamp-1">{r.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-bold text-slate-700">{r.downloads}</p>
                          <p className="text-xs text-slate-400">downloads</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEditResourceModal(r)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-600 transition-colors cursor-pointer"
                            title="Edit resource"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteResource(r.id)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                            title="Delete resource"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {resources.length === 0 && (
                <div className="bg-white rounded-xl border border-slate-100 p-10 flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-slate-50 rounded-xl">
                    <i className="ri-folder-open-line text-slate-300 text-2xl"></i>
                  </div>
                  <p className="text-slate-500 text-sm font-medium">No resources yet</p>
                  <p className="text-slate-400 text-xs">Click &quot;Upload Resource&quot; to add your first resource</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'messages':
        return (
          <div className="flex flex-col gap-5">
            <div>
              <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Messages</h2>
              <p className="text-slate-500 text-sm">{tutorMessages.filter((m) => m.unread).length} unread messages</p>
            </div>
            <div className="flex flex-col gap-3">
              {tutorMessages.map((msg) => (
                <div key={msg.id} className={`bg-white rounded-xl border p-5 ${msg.unread ? 'border-emerald-200' : 'border-slate-100'}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold ${msg.unread ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {msg.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-800 text-sm">{msg.from}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {msg.unread && <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5 rounded-full">New</span>}
                          <span className="text-xs text-slate-400">{msg.time}</span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{msg.message}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pl-12">
                    <input
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type a reply..."
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                    />
                    <button
                      onClick={() => { setReplySent(true); setReplyText(''); setTimeout(() => setReplySent(false), 2000); }}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {replySent ? 'Sent!' : 'Reply'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="flex flex-col gap-5 max-w-2xl">
            <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>My Profile</h2>
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center gap-5 mb-6 pb-6 border-b border-slate-100">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-slate-100">
                  <img src={tutorProfile.avatar} alt="" className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-lg">{tutorProfile.name}</p>
                  <p className="text-slate-500 text-sm">{tutorProfile.email}</p>
                  <div className="flex gap-2 mt-2">
                    {tutorProfile.boards.map((b) => (
                      <span key={b} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{b}</span>
                    ))}
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{tutorProfile.level}</span>
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                  <span className="font-bold text-slate-800">{tutorProfile.rating}</span>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {[
                  { label: 'Full Name', value: tutorProfile.name },
                  { label: 'Email', value: tutorProfile.email },
                  { label: 'Subject', value: tutorProfile.subject },
                  { label: 'Level', value: tutorProfile.level },
                ].map((field) => (
                  <div key={field.label}>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{field.label}</label>
                    <input defaultValue={field.value} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Bio</label>
                  <textarea defaultValue={tutorProfile.bio} rows={3} maxLength={500} className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 resize-none" />
                </div>
                <button className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap w-fit">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <TutorSidebar activeSection={activeSection} onSelect={setActiveSection} />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        {renderSection()}
      </main>
      <SessionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSessionSubmit}
        mode={modalMode}
        initialData={editingSession ? {
          ...editingSession,
          topic: editingSession.topic ?? '',
          sessionType: editingSession.sessionType ?? 'Live Revision',
          platform: editingSession.platform ?? 'Zoom',
          meetingLink: editingSession.meetingLink ?? '',
        } : undefined}
      />
      <ResourceModal
        isOpen={resourceModalOpen}
        onClose={() => setResourceModalOpen(false)}
        onSubmit={handleResourceSubmit}
        mode={resourceModalMode}
        initialData={editingResource ? {
          title: editingResource.title,
          resourceType: editingResource.type,
          subject: editingResource.subject ?? '',
          level: editingResource.level ?? 'GCSE',
          board: editingResource.board ?? 'AQA',
          visibility: editingResource.visibility ?? 'Students Only',
          description: editingResource.description ?? '',
          tags: editingResource.tags ?? '',
        } : undefined}
      />
    </div>
  );
}
